import { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePlatform } from '../context/PlatformContext';
import { debugWarn } from '../utils/debugLog';

/**
 * PlatformInitializer Component
 *
 * Initializes platform data when user logs in, including:
 * - Tenant information
 * - Tenant features
 * - Tenant theme (critical for UI rendering)
 * - Tenant navigation
 *
 * Non-blocking: renders children immediately while initializing in background.
 * Allows rendering with defaults if some calls fail.
 */
const PlatformInitializer = ({ children }) => {
  const { tenant, isAuthenticated, authInitialized } = useAuth();
  const { initializePlatform } = usePlatform();

  // Initialize platform when user is authenticated and tenant is available
  useEffect(() => {
    // Only initialize after auth has been determined
    if (!authInitialized) return;

    const initPlatform = async () => {
      if (isAuthenticated && (tenant?.tenant_id || tenant?.id)) {
        try {
          // Initialize platform (tenant_id now comes from auth context)
          await initializePlatform();
        } catch (error) {
          debugWarn(
            'Platform initialization had issues, but continuing:',
            error
          );
          // Allow rendering even if initialization fails (use default theme)
        }
      }
    };

    initPlatform();
  }, [
    isAuthenticated,
    authInitialized,
    tenant?.tenant_id,
    tenant?.id,
    initializePlatform,
  ]);

  // Non-blocking: always render children immediately while initializing in background
  return children;
};

export default PlatformInitializer;
